import React, { useState, useEffect } from 'react';
import { Building, Heart, GraduationCap, CheckCircle2, Clock } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Projects = () => {
  const [projectsData, setProjectsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    totalBudget: 'K11.0M'
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects`);
      const projects = response.data;
      setProjectsData(projects);
      
      // Calculate stats
      const completed = projects.filter(p => p.status === 'Completed').length;
      const inProgress = projects.filter(p => p.status === 'In Progress').length;
      
      setStats({
        total: projects.length,
        completed: completed,
        inProgress: inProgress,
        totalBudget: 'K11.0M'
      });
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Infrastructure':
        return Building;
      case 'Health':
        return Heart;
      case 'Education':
        return GraduationCap;
      default:
        return Building;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'In Progress':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Planning':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Development Projects
          </h1>
          <p className="text-lg text-gray-600">
            Active and completed projects transforming Hiri Koiari District
          </p>
        </div>
      </section>

      {/* Stats Cards */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-blue-50 rounded-2xl p-8 text-center">
              <div className="text-5xl font-bold text-blue-600 mb-2">{stats.total}</div>
              <div className="text-gray-600 font-medium">Total Projects</div>
            </div>
            <div className="bg-green-50 rounded-2xl p-8 text-center">
              <div className="text-5xl font-bold text-green-600 mb-2">{stats.completed}</div>
              <div className="text-gray-600 font-medium">Completed</div>
            </div>
            <div className="bg-yellow-50 rounded-2xl p-8 text-center">
              <div className="text-5xl font-bold text-yellow-600 mb-2">{stats.inProgress}</div>
              <div className="text-gray-600 font-medium">In Progress</div>
            </div>
            <div className="bg-purple-50 rounded-2xl p-8 text-center">
              <div className="text-5xl font-bold text-purple-600 mb-2">{stats.totalBudget}</div>
              <div className="text-gray-600 font-medium">Total Investment</div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects List */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="text-lg text-gray-600">Loading projects...</div>
            </div>
          ) : projectsData.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-lg text-gray-600">No projects available yet.</p>
            </div>
          ) : (
            projectsData.map((project) => {
              const CategoryIcon = getCategoryIcon(project.category);
              return (
                <div
                  key={project.id}
                  className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between gap-6">
                    {/* Left Section with Icon and Details */}
                    <div className="flex items-start gap-6 flex-1">
                      {/* Icon */}
                      <div className="w-16 h-16 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <CategoryIcon className="w-8 h-8 text-white" />
                      </div>

                      {/* Project Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-2xl font-bold text-gray-900">{project.title}</h3>
                          <span className={`inline-flex items-center space-x-2 px-4 py-1 rounded-full text-sm font-semibold border ${getStatusColor(project.status)}`}>
                            <Clock className="w-4 h-4" />
                            <span>{project.status}</span>
                          </span>
                        </div>
                        
                        <p className="text-gray-500 text-sm mb-4">{project.category}</p>
                        
                        <p className="text-gray-700 leading-relaxed mb-6">{project.description}</p>

                        {/* Progress Bar */}
                        <div className="mb-4">
                          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                            <div
                              className="bg-red-500 h-3 rounded-full transition-all duration-500"
                              style={{ width: `${project.completion}%` }}
                            ></div>
                          </div>
                        </div>

                        {/* Dates */}
                        <div className="flex items-center gap-6 text-sm text-gray-600">
                          <span><strong>Start Date:</strong> {project.start_date}</span>
                          <span><strong>Expected Completion:</strong> {project.expected_completion}</span>
                        </div>
                      </div>
                    </div>

                    {/* Right Section with Budget and Completion */}
                    <div className="text-right flex-shrink-0">
                      <div className="mb-6">
                        <div className="text-sm text-gray-500 mb-1">Budget</div>
                        <div className="text-2xl font-bold text-gray-900">{project.budget}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 mb-1">Completion</div>
                        <div className="text-3xl font-bold text-red-500">{project.completion}%</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
};

export default Projects;
